/**
 * FHIR helpers for in-app notifications created by the Rules Engine.
 *
 * ## Communication differentiation: notifications vs messages
 *
 * A Communication is a **Rules Engine in-app notification** when:
 *   - `medium` includes system `communication-medium` / code `in-app`
 *   - AND `reasonCode` includes system `rules-engine` / code
 *     `rules-engine-in-app-notification`  OR  `meta.tag` includes the same
 *
 * A Communication is a **generic in-app notification** when:
 *   - `medium` includes `in-app` (may not have the rules-engine reason)
 *
 * A Communication is a **message / chat** when:
 *   - It does NOT use medium `in-app` with the notification reason/category.
 *   - Messages live in the Messages product and are excluded from the bell
 *     popover query by the `medium = in-app` search parameter.
 *
 * Notification lifecycle:
 *   - Unread:  status === "in-progress"
 *   - Read:    status === "completed"
 *   - Title:   topic.text
 *   - Body:    payload[0].contentString
 *   - Time:    sent
 *   - Target:  rules-engine-link-target extension → about[0] → subject
 */

import type { Communication, Reference } from '@medplum/fhirtypes';

export const NOTIF = {
  MEDIUM_SYSTEM: 'https://unify.health/fhir/CodeSystem/communication-medium',
  MEDIUM_CODE: 'in-app',
  REASON_SYSTEM: 'https://unify.health/fhir/CodeSystem/rules-engine',
  REASON_CODE: 'rules-engine-in-app-notification',
  CATEGORY_SYSTEM: 'https://unify.health/fhir/CodeSystem/notification-category',
  LINK_TARGET_EXT: 'https://unify.health/fhir/StructureDefinition/rules-engine-link-target',
  LIFECYCLE_REASON_SYSTEM: 'https://unify.health/fhir/CodeSystem/notification-lifecycle',
  LIFECYCLE_REASON_CODE: 'lifecycle-notification',
  MESSAGES_CATEGORY: 'messages',
} as const;

// ---------------------------------------------------------------------------
// Type guards — Communication differentiation
// ---------------------------------------------------------------------------

/** True when medium includes the in-app notification coding. */
export function isInAppCommunication(comm: Communication): boolean {
  return (
    comm.medium?.some(
      (m) =>
        m.coding?.some(
          (c) => c.system === NOTIF.MEDIUM_SYSTEM && c.code === NOTIF.MEDIUM_CODE,
        ),
    ) ?? false
  );
}

/**
 * True when the Communication is specifically a Rules Engine in-app notification.
 * Checks medium + (reasonCode OR meta.tag).
 */
export function isRulesEngineInAppNotification(comm: Communication): boolean {
  if (!isInAppCommunication(comm)) return false;

  const hasReason = comm.reasonCode?.some(
    (rc) =>
      rc.coding?.some(
        (c) => c.system === NOTIF.REASON_SYSTEM && c.code === NOTIF.REASON_CODE,
      ),
  );
  if (hasReason) return true;

  const hasTag = comm.meta?.tag?.some(
    (t) => t.system === NOTIF.REASON_SYSTEM && t.code === NOTIF.REASON_CODE,
  );
  return hasTag ?? false;
}

/**
 * True when the Communication is any kind of in-app notification (Rules Engine
 * or otherwise), excluding messaging duplicates.
 */
export function isNotificationCommunication(comm: Communication): boolean {
  return isInAppCommunication(comm) && !isMessagingInAppNotification(comm);
}

/** In-app notification for chat messages (shown in Messages inbox, not the bell). */
export function isMessagingInAppNotification(comm: Communication): boolean {
  if (!isInAppCommunication(comm)) return false;

  const categoryCode = comm.category?.[0]?.coding?.find(
    (c) => c.system === NOTIF.CATEGORY_SYSTEM,
  )?.code;
  return categoryCode === NOTIF.MESSAGES_CATEGORY;
}

/**
 * True when the Communication is a user-to-user message (chat) rather than a
 * notification. Messages do NOT use medium `in-app` with the notification
 * reason/category.
 */
export function isMessageCommunication(comm: Communication): boolean {
  return !isInAppCommunication(comm);
}

// ---------------------------------------------------------------------------
// Field accessors
// ---------------------------------------------------------------------------

export function isUnreadNotification(comm: Communication): boolean {
  return comm.status === 'in-progress';
}

export function getNotificationTitle(comm: Communication): string {
  return comm.topic?.text?.trim() || 'Notification';
}

export function getNotificationBody(comm: Communication): string {
  const content = comm.payload?.[0]?.contentString;
  return content?.trim() || '';
}

export function getNotificationTimestamp(comm: Communication): string | undefined {
  return comm.sent;
}

/**
 * Resolve the navigation target reference from a notification Communication.
 * Priority: rules-engine-link-target extension → about[0] → subject.
 */
export function getNotificationTargetReference(comm: Communication): Reference | undefined {
  const linkTargetExt = comm.extension?.find((e) => e.url === NOTIF.LINK_TARGET_EXT);
  if (linkTargetExt?.valueReference?.reference) {
    return linkTargetExt.valueReference;
  }

  const aboutRef = comm.about?.[0];
  if (aboutRef?.reference) {
    return aboutRef;
  }

  if (comm.subject?.reference) {
    return comm.subject;
  }

  return undefined;
}

export interface ResolveNotificationRouteOptions {
  /** When true, map targets to client-portal routes instead of dashboard routes. */
  clientPortal?: boolean;
}

/**
 * Map a FHIR reference to an in-app route. Uses known route patterns from the app router.
 * Returns undefined if no suitable route can be determined.
 */
export function resolveNotificationRoute(
  ref: Reference | undefined,
  opts?: ResolveNotificationRouteOptions,
): string | undefined {
  if (!ref?.reference) return undefined;

  const [resourceType, id] = ref.reference.split('/');
  if (!resourceType || !id) return undefined;

  if (opts?.clientPortal) {
    switch (resourceType) {
      case 'Communication':
        return `/client-portal/messages/${id}`;
      case 'Task':
        return `/client-portal/assessments/${id}`;
      case 'Appointment':
        return '/client-portal';
      case 'Patient':
        return '/client-portal/documents';
      default:
        return undefined;
    }
  }

  switch (resourceType) {
    case 'Communication':
      return `/dashboard/messages/${id}`;
    case 'Appointment':
      return `/dashboard/appointments/${id}`;
    case 'Patient':
      return `/dashboard/clients/${id}`;
    case 'EpisodeOfCare':
      return `/dashboard/caseload`;
    case 'ServiceRequest':
      return `/dashboard/episode-tracker`;
    case 'QuestionnaireResponse':
      return `/dashboard/tasks`;
    case 'Task':
      return `/dashboard/tasks`;
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// Recipient reference resolution
// ---------------------------------------------------------------------------

/**
 * Get the recipient reference string for the current profile.
 * Staff/admin → Practitioner/{id}, clients → Patient/{id}.
 *
 * When a `portalPatientId` is provided (from PortalSubjectContext), it takes
 * precedence for Patient profiles so notifications are scoped to the active
 * portal account rather than the login-only profile.
 */
export function getCurrentNotificationRecipientReference(
  profile: { resourceType: string; id?: string } | undefined,
  portalPatientId?: string,
): string | undefined {
  if (!profile?.id) return undefined;

  // In-app notifications are addressed to Patient/{id}. Client portal logins (Patient,
  // RelatedPerson, guarantor proxy) pass portalPatientId for the active chart.
  if (
    portalPatientId
    && (profile.resourceType === 'Patient' || profile.resourceType === 'RelatedPerson')
  ) {
    return `Patient/${portalPatientId}`;
  }

  if (profile.resourceType === 'Patient') {
    return `Patient/${profile.id}`;
  }

  return `${profile.resourceType}/${profile.id}`;
}

// ---------------------------------------------------------------------------
// Search parameter builders
// ---------------------------------------------------------------------------

const IN_APP_MEDIUM_TOKEN = `${NOTIF.MEDIUM_SYSTEM}|${NOTIF.MEDIUM_CODE}`;

/**
 * WebSocket subscription criteria for in-app notifications.
 *
 * Patient profiles use `subject=` (not `recipient=` + `medium=`) to avoid
 * meta.account compartment gating — same constraint called out for messaging
 * in `useMessagingRealtime.tsx`. Search/refetch still filters by recipient + medium.
 */
export function buildNotificationSubscriptionCriteria(recipientRef: string): string {
  const [resourceType] = recipientRef.split('/');
  if (resourceType === 'Patient') {
    return `Communication?subject=${encodeURIComponent(recipientRef)}`;
  }
  return `Communication?recipient=${encodeURIComponent(recipientRef)}`;
}

/** True when the Communication is an in-app notification for this recipient. */
export function isNotificationForRecipient(comm: Communication, recipientRef: string): boolean {
  if (!isNotificationCommunication(comm)) return false;
  if (comm.recipient?.some((r) => r.reference === recipientRef)) return true;
  // WS may deliver via subject= before recipient is indexed; Patient portal uses Patient/{id} for both.
  if (recipientRef.startsWith('Patient/') && comm.subject?.reference === recipientRef) return true;
  return false;
}

/** Medplum search parameters for in-app notifications for a given recipient. */
export function buildNotificationSearchParams(
  recipientRef: string,
  opts?: { unreadOnly?: boolean; count?: number },
): Record<string, string> {
  const params: Record<string, string> = {
    recipient: recipientRef,
    medium: IN_APP_MEDIUM_TOKEN,
    _sort: '-sent',
    _count: String(opts?.count ?? 50),
  };
  if (opts?.unreadOnly) {
    params.status = 'in-progress';
  }
  return params;
}
